import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { WorkflowExecution, ExecutionFilters, ExecutionLogEntry } from "@/types/workflow";
import { Json } from "@/integrations/supabase/types";

// Type guard for execution log entries
const parseExecutionLog = (log: Json | null): ExecutionLogEntry[] | null => {
  if (!log) return null;
  if (Array.isArray(log)) {
    return log as unknown as ExecutionLogEntry[];
  }
  return null;
};

export const useWorkflowExecutions = (filters?: ExecutionFilters) => {
  return useQuery({
    queryKey: ["workflow-executions", filters],
    queryFn: async () => {
      console.log("[useWorkflowExecutions] Fetching executions with filters:", filters);

      let query = supabase
        .from("workflow_executions")
        .select(`
          *,
          workflow:workflows(id, name, description, is_active)
        `)
        .order("started_at", { ascending: false })
        .limit(100);

      // Apply filters
      if (filters?.status && filters.status.length > 0) {
        query = query.in("status", filters.status);
      }

      if (filters?.workflow_id) {
        query = query.eq("workflow_id", filters.workflow_id);
      }

      if (filters?.date_from) {
        query = query.gte("started_at", filters.date_from);
      }

      if (filters?.date_to) {
        query = query.lte("started_at", filters.date_to);
      }

      const { data, error } = await query;

      if (error) {
        console.error("[useWorkflowExecutions] Error:", error);
        throw error;
      }

      console.log("[useWorkflowExecutions] Fetched", data?.length || 0, "executions");
      
      // Transform the data to match our types
      return (data || []).map(execution => ({
        ...execution,
        execution_log: parseExecutionLog(execution.execution_log)
      })) as WorkflowExecution[];
    },
    staleTime: 10000, // 10 seconds
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });
};

export const useWorkflowExecution = (executionId: string | null) => {
  return useQuery({
    queryKey: ["workflow-execution", executionId],
    queryFn: async () => {
      if (!executionId) return null;

      const { data, error } = await supabase
        .from("workflow_executions")
        .select(`
          *,
          workflow:workflows(*)
        `)
        .eq("id", executionId)
        .single();

      if (error) {
        console.error("[useWorkflowExecution] Error:", error);
        throw error;
      }

      return {
        ...data,
        execution_log: parseExecutionLog(data.execution_log)
      } as WorkflowExecution;
    },
    enabled: !!executionId,
    staleTime: 5000,
  });
};

export const useActiveExecutions = () => {
  return useQuery({
    queryKey: ["active-executions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("workflow_executions")
        .select(`
          *,
          workflow:workflows(id, name)
        `)
        .in("status", ["pending", "running", "waiting"])
        .order("started_at", { ascending: false });

      if (error) {
        console.error("[useActiveExecutions] Error:", error);
        throw error;
      }

      const executions = (data || []).map(execution => ({
        ...execution,
        execution_log: parseExecutionLog(execution.execution_log)
      })) as WorkflowExecution[];

      // Auto-cleanup: mark executions running > 10 minutes as failed
      const staleThreshold = Date.now() - 10 * 60 * 1000;
      const staleExecutions = executions.filter(
        e => e.status === "running" && new Date(e.started_at).getTime() < staleThreshold
      );

      if (staleExecutions.length > 0) {
        console.log(`[useActiveExecutions] Found ${staleExecutions.length} stale executions, marking as failed`);
        for (const stale of staleExecutions) {
          await supabase
            .from("workflow_executions")
            .update({
              status: "failed",
              completed_at: new Date().toISOString(),
              error_message: "Edge Function timeout - automatisch opgeruimd",
            })
            .eq("id", stale.id)
            .eq("status", "running"); // Safety: only update if still running
        }
        // Return non-stale executions only
        return executions.filter(e => !staleExecutions.some(s => s.id === e.id));
      }

      return executions;
    },
    staleTime: 5000,
    refetchInterval: 5000,
  });
};

export const useExecutionMetrics = (workflowId?: string) => {
  return useQuery({
    queryKey: ["execution-metrics", workflowId],
    queryFn: async () => {
      let query = supabase
        .from("workflow_executions")
        .select("id, status, started_at, completed_at");

      if (workflowId) {
        query = query.eq("workflow_id", workflowId);
      }

      // Get last 24 hours
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      query = query.gte("started_at", oneDayAgo);

      const { data, error } = await query;

      if (error) {
        console.error("[useExecutionMetrics] Error:", error);
        throw error;
      }

      const executions = data || [];
      const successful = executions.filter(e => e.status === "success").length;
      const failed = executions.filter(e => e.status === "failed").length;

      // Calculate average duration for completed executions
      const completedWithDuration = executions.filter(
        e => e.completed_at && e.started_at
      );
      const avgDuration = completedWithDuration.length > 0
        ? completedWithDuration.reduce((acc, e) => {
            const duration = new Date(e.completed_at!).getTime() - new Date(e.started_at).getTime();
            return acc + duration;
          }, 0) / completedWithDuration.length
        : 0;

      const lastExecution = executions.length > 0
        ? executions.sort((a, b) => 
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
          )[0].started_at
        : null;

      return {
        total_executions: executions.length,
        successful,
        failed,
        avg_duration_ms: Math.round(avgDuration),
        last_execution: lastExecution,
      };
    },
    staleTime: 30000,
  });
};

export interface DurationTrendData {
  period: string;
  avg_duration_ms: number;
  min_duration_ms: number;
  max_duration_ms: number;
  execution_count: number;
}

export const useExecutionDurationTrend = (workflowId?: string, hours: number = 24) => {
  return useQuery({
    queryKey: ["execution-duration-trend", workflowId, hours],
    queryFn: async (): Promise<DurationTrendData[]> => {
      let query = supabase
        .from("workflow_executions")
        .select("started_at, completed_at, status")
        .in("status", ["success", "failed"]) // Only completed executions
        .not("completed_at", "is", null);

      if (workflowId) {
        query = query.eq("workflow_id", workflowId);
      }

      // Get last X hours
      const startDate = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
      query = query.gte("started_at", startDate).order("started_at", { ascending: true });

      const { data, error } = await query;

      if (error) {
        console.error("[useExecutionDurationTrend] Error:", error);
        throw error;
      }

      const executions = data || [];
      
      // Group by hour
      const hourlyGroups = new Map<string, { durations: number[] }>();
      
      executions.forEach(e => {
        if (!e.completed_at || !e.started_at) return;
        
        const duration = new Date(e.completed_at).getTime() - new Date(e.started_at).getTime();
        const hourKey = new Date(e.started_at).toISOString().slice(0, 13) + ":00:00.000Z";
        
        if (!hourlyGroups.has(hourKey)) {
          hourlyGroups.set(hourKey, { durations: [] });
        }
        hourlyGroups.get(hourKey)!.durations.push(duration);
      });

      // Convert to trend data
      const trendData: DurationTrendData[] = [];
      hourlyGroups.forEach((group, period) => {
        const durations = group.durations;
        if (durations.length === 0) return;
        
        trendData.push({
          period,
          avg_duration_ms: Math.round(durations.reduce((a, b) => a + b, 0) / durations.length),
          min_duration_ms: Math.min(...durations),
          max_duration_ms: Math.max(...durations),
          execution_count: durations.length,
        });
      });

      // Sort by period
      trendData.sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime());
      
      return trendData;
    },
    staleTime: 60000, // 1 minute
  });
};

export const useCancelExecution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (executionId: string) => {
      const { error } = await supabase
        .from("workflow_executions")
        .update({ 
          status: "cancelled",
          completed_at: new Date().toISOString()
        })
        .eq("id", executionId)
        .in("status", ["pending", "running", "waiting"]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflow-executions"] });
      queryClient.invalidateQueries({ queryKey: ["active-executions"] });
    },
  });
};
